import { Link, Form } from "@remix-run/react";
import { useAuth } from "~/lib/auth-context";
import { useNavigation, navigationConfig } from "~/lib/navigation-utils";
import { AuthAwareLink } from "~/components/ui/AuthAwareLink";

interface HeaderProps {
  onOpenSidebar?: () => void;
}

export function Header({ onOpenSidebar }: HeaderProps) {
  const { user, loading, isAuthenticated, signOut } = useAuth();
  const { isActive } = useNavigation();

  // const [isMenuOpen, setIsMenuOpen] = useState(false);

  // const toggleMenu = () => {
  //   setIsMenuOpen(!isMenuOpen);
  // };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and brand */}
          <div className="flex items-center">
            {onOpenSidebar && (
              <button
                onClick={onOpenSidebar}
                className="mr-3 p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 dark:text-gray-300 dark:hover:text-gray-100 dark:hover:bg-gray-700"
                aria-label="메뉴 열기"
              >
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              </button>
            )}
            <Link to="/" className="flex items-center">
              <img
                src="/logo-light.png"
                alt="Slowdive"
                className="h-12 w-auto mr-2 block dark:hidden"
                width="auto"
                height={48}
                loading="eager"
                decoding="async"
              />
              <img
                src="/logo-dark.png"
                alt="Slowdive"
                className="h-12 w-auto mr-2 hidden dark:block"
                width="auto"
                height={48}
                loading="eager"
                decoding="async"
              />
              {/* <span className="text-xl font-bold text-gray-900">slowdive</span>  */}
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            {navigationConfig
              .filter((item) => {
                // Only hide admin items for non-admin users
                if (item.adminOnly && user?.profile?.role !== "admin") {
                  return false;
                }
                // Exclude home and profile from main nav
                return item.id !== "home" && item.id !== "profile";
              })
              .map((item) => {
                const active = isActive(item);
                return (
                  <AuthAwareLink
                    key={item.id}
                    to={item.href}
                    requiresAuth={item.requiresAuth}
                    authMessage={item.authRedirectMessage}
                    prefetch="intent"
                    className={`font-medium transition-colors flex items-center relative ${
                      active
                        ? "text-blue-600 dark:text-blue-400"
                        : "text-gray-700 hover:text-blue-600 dark:text-gray-300 dark:hover:text-blue-400"
                    }`}
                  >
                    {item.label}
                    {active && (
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
                    )}
                  </AuthAwareLink>
                );
              })}
          </nav>

          {/* User menu / Auth buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {loading ? (
              <div className="text-gray-500 dark:text-gray-400">로딩 중...</div>
            ) : user ? (
              <div className="flex items-center space-x-4">
                <span className="text-gray-700 dark:text-gray-300">
                  안녕하세요, {user.profile?.name || user.email}님
                </span>
                <Link
                  to="/profile"
                  prefetch="intent"
                  className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                >
                  프로필
                </Link>
                <Form
                  method="post"
                  action="/logout"
                  onSubmit={async (e) => {
                    // Call client-side signOut first
                    await signOut();
                    // Let the form submission continue to the server-side logout
                  }}
                >
                  <button
                    type="submit"
                    className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600 transition-colors dark:bg-red-400 dark:hover:bg-red-500 dark:text-gray-900"
                  >
                    로그아웃
                  </button>
                </Form>
              </div>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100"
                >
                  로그인
                </Link>
                <Link
                  to="/signup"
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors dark:bg-blue-400 dark:hover:bg-blue-500 dark:text-gray-900"
                >
                  회원가입
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button - 주석처리 */}
          {/* <div className="md:hidden flex items-center">
            <button
              onClick={toggleMenu}
              className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              aria-expanded="false"
            >
              <span className="sr-only">메뉴 열기</span>
              <svg
                className={`${isMenuOpen ? "hidden" : "block"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
              <svg
                className={`${isMenuOpen ? "block" : "hidden"} h-6 w-6`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div> */}
        </div>
      </div>

      {/* Mobile menu - 주석처리 */}
      {/* <div className={`${isMenuOpen ? "block" : "hidden"} md:hidden`}>
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
          <Link
            to="/recipes"
            prefetch="intent"
            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            onClick={() => setIsMenuOpen(false)}
          >
            레시피
          </Link>
          <Link
            to="/articles"
            prefetch="intent"
            className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            onClick={() => setIsMenuOpen(false)}
          >
            건강정보
          </Link>
          <Link
            to="/recommendations"
            prefetch="intent"
            className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
            onClick={() => setIsMenuOpen(false)}
          >
            맞춤형 추천
          </Link>
          {user && (
            <Link
              to="/favorites"
              prefetch="intent"
              className="flex items-center px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              즐겨찾기
            </Link>
          )}
          {user?.profile?.role === "admin" && (
            <Link
              to="/admin"
              prefetch="intent"
              className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(false)}
            >
              관리자
            </Link>
          )}
        </div>
        <div className="pt-4 pb-3 border-t border-gray-200">
          {loading ? (
            <div className="px-4 py-2 text-gray-500">로딩 중...</div>
          ) : user ? (
            <div className="px-4 py-2 space-y-3">
              <div className="text-base font-medium text-gray-800">
                {user.profile?.name || user.email}
              </div>
              <Link
                to="/profile"
                prefetch="intent"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                프로필
              </Link>
              <Form
                method="post"
                action="/logout"
                onSubmit={async (e) => {
                  setIsMenuOpen(false);
                  await signOut();
                }}
              >
                <button
                  type="submit"
                  className="w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-600 hover:text-red-800 hover:bg-gray-50"
                >
                  로그아웃
                </button>
              </Form>
            </div>
          ) : (
            <div className="px-4 py-2 space-y-3">
              <Link
                to="/login"
                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                onClick={() => setIsMenuOpen(false)}
              >
                로그인
              </Link>
              <Link
                to="/signup"
                className="block px-3 py-2 rounded-md text-base font-medium bg-blue-500 text-white hover:bg-blue-600"
                onClick={() => setIsMenuOpen(false)}
              >
                회원가입
              </Link>
            </div>
          )}
        </div>
      </div> */}
    </header>
  );
}
